import { useState, useEffect, useCallback, useMemo, type SyntheticEvent } from "react";
import {
  Dialog,
  Autocomplete,
  TextField,
  Stack,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Divider,
} from "@mui/material";
import { useGetList, useUpdate, useRefresh, useNotify } from "react-admin";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { Client, Claimant, Policy, StoplossClaim, ResourceName, Reimbursement } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { formatYearRange } from "@/utils/formatters/dateFormatter";
import Button from "@/components/ui/Button";
import { ButtonStyleType, UIComponentSize } from "@/types/shared/uiComponents";
import { Close as CloseIcon } from "@mui/icons-material";

interface ReassignReimbursementDialogProps {
  open: boolean;
  onClose: () => void;
  record: Reimbursement;
}

const safeUSD = (n: unknown) => (typeof n === "number" && !Number.isNaN(n) ? formatUSD(n) : "—");

/**
 * Debounced server-side search state for a picker (#7191).
 *
 * Typing feeds `search` immediately (for UI state) and `debouncedSearch` ~300ms
 * later (for the `q` list filter) — mirrors RecordSelector's debounce (ClientGate,
 * the #5697 precedent, searches without one). `reset` clears BOTH values synchronously: clearing only the raw value
 * would leave the debounced one stale for ~300ms, so a scope change (new client)
 * could fire the next fetch with the old q=.
 */
const useDebouncedSearch = (delayMs = 300) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), delayMs);
    return () => clearTimeout(timer);
  }, [search, delayMs]);

  const reset = useCallback(() => {
    setSearch("");
    setDebouncedSearch("");
  }, []);

  return { search, debouncedSearch, setSearch, reset };
};

/**
 * Keep the selected record present in the options even when it falls outside the
 * freshly-loaded search page, so narrowing the search doesn't drop the value from
 * MUI's options (which trips the "value not in options" warning and unmatches it).
 */
const seedSelected = <T extends { id: string | number }>(options: T[], selected: T | null): T[] =>
  selected && !options.some((o) => o.id === selected.id) ? [selected, ...options] : options;

export const ReassignReimbursementDialog = ({ open, onClose, record }: ReassignReimbursementDialogProps) => {
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedClaimant, setSelectedClaimant] = useState<Claimant | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [selectedClaim, setSelectedClaim] = useState<StoplossClaim | null>(null);

  const [update, { isPending }] = useUpdate();
  const refresh = useRefresh();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();

  // Server-side search per picker (#7191): fixed-cap page-1 fetches made any
  // record beyond the cap silently unreachable (Lockton: 1,094 claimants on one
  // client vs a 500 cap). Typing now drives a debounced `q` list filter — the
  // #5697 ClientGate / RecordSelector pattern — so every record is reachable
  // regardless of tenant size, and raising caps (which the backend may clamp
  // anyway, #5166) is unnecessary.
  const clientSearch = useDebouncedSearch();
  const claimantSearch = useDebouncedSearch();
  const policySearch = useDebouncedSearch();
  const claimSearch = useDebouncedSearch();

  // Reset on open
  useEffect(() => {
    if (open) {
      setSelectedClient(null);
      setSelectedClaimant(null);
      setSelectedPolicy(null);
      setSelectedClaim(null);
      clientSearch.reset();
      claimantSearch.reset();
      policySearch.reset();
      claimSearch.reset();
    }
    // reset fns are stable useCallbacks — effect only fires on open change
  }, [open, clientSearch.reset, claimantSearch.reset, policySearch.reset, claimSearch.reset]);

  const clientQ = clientSearch.debouncedSearch.trim();
  const claimantQ = claimantSearch.debouncedSearch.trim();
  const policyQ = policySearch.debouncedSearch.trim();
  const claimQ = claimSearch.debouncedSearch.trim();

  // Fetch clients (server-side q search)
  const { data: clients = [], isFetching: loadingClients } = useGetList<Client>(
    ResourceName.CLIENTS,
    {
      filter: clientQ ? { q: clientQ } : {},
      pagination: { page: 1, perPage: 200 },
      sort: { field: "client_legal_name", order: "ASC" },
    },
    { enabled: open }
  );

  // Fetch claimants scoped to selected client (server-side q search)
  const { data: claimants = [], isFetching: loadingClaimants } = useGetList<Claimant>(
    ResourceName.CLAIMANTS,
    {
      filter: {
        ...(selectedClient ? { client_id_ref: selectedClient.id } : {}),
        ...(claimantQ ? { q: claimantQ } : {}),
      },
      pagination: { page: 1, perPage: 500 },
      sort: { field: "claimant_name", order: "ASC" },
    },
    { enabled: !!selectedClient }
  );

  // Fetch policies scoped to selected client (server-side q search)
  const { data: policies = [], isFetching: loadingPolicies } = useGetList<Policy>(
    ResourceName.POLICIES,
    {
      filter: {
        ...(selectedClient ? { client_id_ref: selectedClient.id } : {}),
        ...(policyQ ? { q: policyQ } : {}),
      },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "effective_start_date", order: "DESC" },
    },
    { enabled: !!selectedClient }
  );

  // Fetch claims scoped to selected claimant + client + policy (server-side q search)
  const claimFilter = {
    ...(selectedClaimant ? { claimant_id_ref: selectedClaimant.id } : {}),
    ...(selectedClient ? { client_id_ref: selectedClient.id } : {}),
    ...(selectedPolicy ? { policy_id_ref: selectedPolicy.id } : {}),
    ...(claimQ ? { q: claimQ } : {}),
  };
  const { data: claims = [], isFetching: loadingClaims } = useGetList<StoplossClaim>(
    ResourceName.STOPLOSS_CLAIMS,
    {
      filter: claimFilter,
      pagination: { page: 1, perPage: 200 },
      sort: { field: "created_datetime", order: "DESC" },
    },
    { enabled: !!selectedClaimant, keepPreviousData: false }
  );

  // Clear dependent selections (and their search text) when a parent changes, so a
  // scope switch never fetches the new scope with the previous scope's q=. This
  // runs in the change handlers, not an effect: an effect resets only AFTER the
  // render that already fired the new-scope fetch with the stale q.
  const handleClientChange = (client: Client | null) => {
    setSelectedClient(client);
    setSelectedClaimant(null);
    setSelectedPolicy(null);
    setSelectedClaim(null);
    claimantSearch.reset();
    policySearch.reset();
    claimSearch.reset();
  };

  const handleClaimantChange = (claimant: Claimant | null) => {
    setSelectedClaimant(claimant);
    setSelectedClaim(null);
    claimSearch.reset();
  };

  const handlePolicyChange = (policy: Policy | null) => {
    setSelectedPolicy(policy);
    setSelectedClaim(null);
    claimSearch.reset();
  };

  // Keep the selected record in options even when the narrowed search page no
  // longer contains it (AC: selected values stay stable while searching).
  const clientOptions = useMemo(() => seedSelected(clients, selectedClient), [clients, selectedClient]);
  const claimantOptions = useMemo(() => seedSelected(claimants, selectedClaimant), [claimants, selectedClaimant]);
  const policyOptions = useMemo(() => seedSelected(policies, selectedPolicy), [policies, selectedPolicy]);
  const claimOptions = useMemo(() => seedSelected(claims, selectedClaim), [claims, selectedClaim]);

  const canSubmit = !!selectedClient && !!selectedClaimant;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      await update(ResourceName.REIMBURSEMENTS, {
        id: record.id,
        data: {
          client_id_ref: selectedClient!.id,
          claimant_id_ref: selectedClaimant!.id,
          ...(selectedPolicy ? { policy_id_ref: selectedPolicy.id } : {}),
          ...(selectedClaim ? { claim_id_ref: selectedClaim.id } : {}),
        },
        previousData: record,
      });
      notify("Reimbursement reassigned successfully", { type: "success" });
      refresh();
      onClose();
    } catch (error) {
      notifyError(error);
    }
  };

  const getPolicyLabel = (policy: Policy) => {
    const carrier =
      (policy as Record<string, string>).vendor_company_name || policy.source_data?.stop_loss_carrier || "No Carrier";
    const years = formatYearRange(policy.effective_start_date, policy.effective_end_date);
    return [carrier, years, formatToTitleCase(policy.status)].filter(Boolean).join(" · ");
  };

  const getClaimLabel = (claim: StoplossClaim) =>
    `${formatToTitleCase(claim.stoploss_claim_type || "Claim")} · ${claim.total_paid_amount ? safeUSD(claim.total_paid_amount) : "—"} · ${formatToTitleCase(claim.claim_status)}`;

  // Treat "typed but debounce hasn't fired yet" as busy too, so the picker shows
  // its loading state instead of flashing "No matches" for the stale query.
  const clientBusy = loadingClients || clientSearch.search.trim() !== clientQ;
  const claimantBusy = loadingClaimants || claimantSearch.search.trim() !== claimantQ;
  const policyBusy = loadingPolicies || policySearch.search.trim() !== policyQ;
  const claimBusy = loadingClaims || claimSearch.search.trim() !== claimQ;

  // Shared server-search Autocomplete props (#5697 pattern): options come back
  // already filtered by the server (each picker also disables client-side
  // filtering via an inline filterOptions identity — inline so MUI's option-type
  // inference survives); the input display stays uncontrolled (the selected label
  // shows after picking) and onInputChange only feeds the server q filter. Only
  // typed edits set q; every other reason (clear, blur, reset, selectOption)
  // clears it — MUI's clearOnBlur empties the box on blur, and a q left behind
  // would silently narrow the list on reopen (the #7191 symptom again).
  const autoProps = (busy: boolean, search: ReturnType<typeof useDebouncedSearch>) => ({
    size: "small" as const,
    loading: busy,
    loadingText: "Searching...",
    onInputChange: (_event: SyntheticEvent, value: string, reason: string) => {
      if (reason === "input") search.setSearch(value);
      else search.reset();
    },
    slotProps: {
      input: {
        endAdornment: busy ? <CircularProgress size={18} /> : undefined,
      },
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <Stack sx={{ p: 2.5 }} spacing={2.5}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight={600}>
            Reassign Reimbursement
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: "text.secondary" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          Select a new client and claimant for this reimbursement. Policy and claim are optional.
        </Typography>

        {/* Required: Client + Claimant */}
        <Stack spacing={2}>
          <Autocomplete
            options={clientOptions}
            filterOptions={(x) => x}
            value={selectedClient}
            onChange={(_, v) => handleClientChange(v)}
            getOptionLabel={(opt) => opt.client_legal_name || opt.id}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderInput={(params) => <TextField {...params} label="Client *" placeholder="Search clients..." />}
            {...autoProps(clientBusy, clientSearch)}
            noOptionsText={clientQ ? "No matching clients" : "No clients found"}
          />

          <Autocomplete
            options={claimantOptions}
            filterOptions={(x) => x}
            value={selectedClaimant}
            onChange={(_, v) => handleClaimantChange(v)}
            getOptionLabel={(opt) => opt.claimant_name || opt.id}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            disabled={!selectedClient}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Claimant *"
                placeholder={selectedClient ? "Search claimants..." : "Select a client first"}
              />
            )}
            {...autoProps(claimantBusy, claimantSearch)}
            noOptionsText={claimantQ ? "No matching claimants" : "No claimants found for this client"}
          />
        </Stack>

        {/* Optional: Policy + Claim */}
        {selectedClaimant && (
          <>
            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Optional
            </Typography>
            <Stack spacing={2}>
              <Autocomplete
                options={policyOptions}
                filterOptions={(x) => x}
                value={selectedPolicy}
                onChange={(_, v) => handlePolicyChange(v)}
                getOptionLabel={getPolicyLabel}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => <TextField {...params} label="Policy" placeholder="Search policies..." />}
                {...autoProps(policyBusy, policySearch)}
                noOptionsText={policyQ ? "No matching policies" : "No policies found for this client"}
              />

              <Autocomplete
                options={claimOptions}
                filterOptions={(x) => x}
                value={selectedClaim}
                onChange={(_, v) => setSelectedClaim(v)}
                getOptionLabel={getClaimLabel}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                  <TextField {...params} label="Stop-Loss Claim" placeholder="Search claims..." />
                )}
                {...autoProps(claimBusy, claimSearch)}
                noOptionsText={claimQ ? "No matching claims" : "No claims found for this claimant"}
              />
            </Stack>
          </>
        )}

        {/* Summary + Actions */}
        {canSubmit && (
          <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1.5 }}>
            <Stack spacing={0.25}>
              <SummaryRow label="Client" value={selectedClient?.client_legal_name} />
              <SummaryRow label="Claimant" value={selectedClaimant?.claimant_name} />
              {selectedPolicy && <SummaryRow label="Policy" value={getPolicyLabel(selectedPolicy)} />}
              {selectedClaim && <SummaryRow label="Claim" value={getClaimLabel(selectedClaim)} />}
            </Stack>
          </Box>
        )}

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button variant={ButtonStyleType.Text} size={UIComponentSize.Small} onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button size={UIComponentSize.Small} onClick={handleSubmit} disabled={!canSubmit || isPending}>
            {isPending ? "Saving..." : "Reassign"}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};

const SummaryRow = ({ label, value }: { label: string; value?: string }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="caption" fontWeight={500}>
      {value || "—"}
    </Typography>
  </Stack>
);
